import type { StateCreator } from 'zustand'
import type { AppState, ReceiptState, OCRResult } from '../types'

export type ReceiptSlice = Pick<
  AppState,
  | 'receipt'
  | 'setCapturedImage'
  | 'setOCRLoading'
  | 'setOCRSuccess'
  | 'setOCRError'
  | 'setEditedTotal'
  | 'getSubtotal'
  | 'getTax'
  | 'getTotal'
  | 'resetReceipt'
>

const initialState: ReceiptState = {
  capturedImage: null,
  ocrResult: { status: 'idle' },
  editedTotal: null,
}

export const createReceiptSlice: StateCreator<
  AppState,
  [['zustand/devtools', never], ['zustand/persist', unknown]],
  [],
  ReceiptSlice
> = (set, get) => ({
  receipt: initialState,

  setCapturedImage: (imageUrl) =>
    set(
      (state) => ({ receipt: { ...state.receipt, capturedImage: imageUrl } }),
      undefined,
      'receipt/setCapturedImage'
    ),

  setOCRLoading: () =>
    set(
      (state) => ({
        receipt: { ...state.receipt, ocrResult: { status: 'loading' } },
      }),
      undefined,
      'receipt/setOCRLoading'
    ),

  setOCRSuccess: (result: OCRResult) =>
    set(
      (state) => ({
        receipt: {
          ...state.receipt,
          ocrResult: { status: 'success', data: result },
        },
      }),
      undefined,
      'receipt/setOCRSuccess'
    ),

  setOCRError: (error) =>
    set(
      (state) => ({
        receipt: { ...state.receipt, ocrResult: { status: 'error', error } },
      }),
      undefined,
      'receipt/setOCRError'
    ),

  setEditedTotal: (total) =>
    set(
      (state) => ({ receipt: { ...state.receipt, editedTotal: total } }),
      undefined,
      'receipt/setEditedTotal'
    ),

  getSubtotal: () => {
    const { ocrResult } = get().receipt
    if (ocrResult.status === 'success') {
      return ocrResult.data.subtotal
    }
    return 0
  },

  getTax: () => {
    const { ocrResult } = get().receipt
    if (ocrResult.status === 'success') {
      return ocrResult.data.tax
    }
    return 0
  },

  getTotal: () => {
    const { ocrResult, editedTotal } = get().receipt
    if (editedTotal !== null) {
      return editedTotal
    }
    if (ocrResult.status === 'success') {
      return ocrResult.data.total
    }
    return 0
  },

  resetReceipt: () =>
    set({ receipt: initialState }, undefined, 'receipt/reset'),
})
